class PickerOptions extends HTMLElement {
  static get observedAttributes() {
    return ['value'];
  }

  constructor() {
    super();
    this.hasMultiple      = this.hasAttribute('multiple');
    this.isHandlingChange = false;
    this._onClick         = this._onClick.bind(this);
    this._onMutations     = this._onMutations.bind(this);
    this.customScrollbar = this.closest('custom-scrollbar');
    this.field = document.getElementById(this.getAttribute('field'));
  }

  connectedCallback() {
    // Délégué de clic
    this.addEventListener('click', this._onClick);

    // Observer les changements de 'selected'
    this._observer = new MutationObserver(this._onMutations);
    this._observer.observe(this, {
      subtree: true,
      attributes: true,
      attributeFilter: ['selected']
    });

    // Init data-* sur la valeur initiale
    this._updateDataAttributes();

    if (this.classList.contains('picker-options-grid')) {
      this._gridElement = this;
    } else {
      this._gridElement = this.querySelector('.picker-options-grid');
    }

    if (this._gridElement) {
      this._originalOrder = Array.from(this._gridElement.querySelectorAll('.picker-option__item'));
    }

    this._handleResize = this._handleResize.bind(this);
    window.addEventListener('resize', this._handleResize);

    this._handleResize();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this._observer.disconnect();
    window.removeEventListener('resize', this._handleResize);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'value' && oldVal !== newVal) {
      this._syncChildren();
      this._updateDataAttributes();
      // Redispatch pour que le composant parent capte le changement
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        composed: true
      }));
    }
  }

  _onClick(e) {
    // quel que soit l'élément interne cliqué, on trouve la picker-option
    const path = e.composedPath ? e.composedPath() : (e.path || []);
    const opt  = /** @type {HTMLElement} **/(
      path.find(el => el.tagName === 'PICKER-OPTION')
    );
    if (!opt) return;
    e.preventDefault();

    if (this.hasMultiple) {
      opt.toggleAttribute('selected');
    } else {
      // désélectionne tout, puis sélectionne
      this.querySelectorAll('picker-option[selected]')
        .forEach(o => o.removeAttribute('selected'));
      opt.setAttribute('selected', '');
    }
  }

  _onMutations(records) {
    // Ne pas empiler plusieurs fois
    if (this.isHandlingChange) return;

    for (const { target } of records) {
      // On ne s'intéresse qu'aux ajouts (hasAttribute('selected') true)
      if (target.tagName !== 'PICKER-OPTION' || !target.hasAttribute('selected'))
        continue;

      this.isHandlingChange = true;

      const val = target.getAttribute('value');
      let values = this.value ? this.value.split(' + ') : [];

      if (this.hasMultiple) {
        if (!values.includes(val)) values.push(val);
      } else {
        values = [val];
      }

      this.value = values.join(' + ');
      if (this.field) this.field.value = values.join(' + ');
      this.isHandlingChange = false;
      break;  // une seule mutation traitée
    }

    if (this.customScrollbar?.startAutoScrollToSelected && this._isVisibleDeep(this)) {
      this.customScrollbar.startAutoScrollToSelected();
    }
  }

  _isVisibleDeep(el) {
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      if (
        el.offsetWidth <= 0 ||
        el.offsetHeight <= 0 ||
        style.display === 'none' ||
        style.visibility === 'hidden'
      ) {
        return false;
      }
      el = el.parentElement;
    }
    return true;
  }

  _handleResize() {
    const isMobile = window.matchMedia('(max-width: 989px)').matches;

    if (!this._gridElement || !this._originalOrder) return;

    // Restaure ordre initial
    this._originalOrder.forEach(el => this._gridElement.appendChild(el));

    const totalItems = this._originalOrder.length;
    const style = getComputedStyle(this);
    const columns = parseInt(style.getPropertyValue('--grid-columns')) || 5;
    const maxRows = parseInt(style.getPropertyValue('--max-rows')) || 2;

    // let rows = 1;
    // if (isMobile && columns < totalItems) {
    //   rows = Math.min(maxRows, Math.ceil(totalItems / columns));
    // }

    let rows = 1;
    if (isMobile && columns < totalItems) {
      let estimatedRows = Math.min(maxRows, Math.ceil(totalItems / columns));

      // Teste la densité horizontale pour voir si on peut réduire le nombre de lignes
      const sampleItem = this._gridElement.querySelector('.picker-option__item');
      const containerWidth = this._gridElement.clientWidth;

      if (sampleItem && estimatedRows > 1) {
        const itemWidth = sampleItem.getBoundingClientRect().width;
        const gapX = parseFloat(getComputedStyle(this._gridElement).columnGap) || 0;

        const itemsPerRow = Math.ceil(totalItems / estimatedRows);
        const totalWidth = (itemWidth * itemsPerRow) + (gapX * (itemsPerRow - 1));

        if (totalWidth < containerWidth * 0.8) {
          // Trop d'espace horizontal : on réduit le nombre de lignes
          estimatedRows--;
        }
      }

      rows = estimatedRows;
    }

    this._gridElement.style.setProperty('--grid-rows', rows);

    if (isMobile && rows > 1) {
      this._reorderItemsForHorizontalGrid(rows);
    }
  }


  _reorderItemsForHorizontalGrid(rows = 2) {
    const grid = this._gridElement;
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll('.picker-option__item'));
    const columns = parseInt(getComputedStyle(this).getPropertyValue('--grid-columns')) || 5;

    if (items.length < 3 || columns < 2) return;

    const reordered = [];
    const perRow = Math.ceil(items.length / rows);

    for (let col = 0; col < perRow; col++) {
      for (let row = 0; row < rows; row++) {
        const index = row * perRow + col;
        if (items[index]) {
          reordered.push(items[index]);
        }
      }
    }

    reordered.forEach(el => grid.appendChild(el));
  }


  _syncChildren() {
    const vals = this.value ? this.value.split(' + ') : [];
    this.querySelectorAll('picker-option').forEach(opt => {
      if (vals.includes(opt.getAttribute('value'))) {
        opt.setAttribute('selected', '');
      } else {
        opt.removeAttribute('selected');
      }
    });
  }

  _updateDataAttributes() {
    for (const attr of this.getAttributeNames()) {
      if (attr.startsWith('data-') && attr !== 'data-scrollable') {
        this.removeAttribute(attr);
      }
    }

    const sel = this.querySelector('picker-option[selected]');
    if (!sel) return;

    for (const attr of sel.getAttributeNames()) {
      if (attr.startsWith('data-') && attr !== 'data-scrollable') {
        this.setAttribute(attr, sel.getAttribute(attr));
      }
    }
  }

  /** Pour le composant parent */
  get name()  { return this.getAttribute('name')  || ''; }
  get value() { return this.getAttribute('value') || ''; }
  set value(v){ this.setAttribute('value', v); }
}

customElements.define('picker-options', PickerOptions);


class PickerOption extends HTMLElement {
  static get observedAttributes() {
    return ['selected'];
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'selected') {
      const sel = newVal !== null;
      this.setAttribute('aria-selected', sel);
    }
  }
}

customElements.define('picker-option', PickerOption);
